import { useState, type ReactNode } from "react";
import { useOutletContext } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { Markdown } from "@/components/Markdown";
import { SettingsChatPanel } from "@/components/SettingsChatPanel";
import { SplitPane } from "@/components/SplitPane";
import { api, scopeKey, type ChatScopeRef } from "@/lib/api";
import { PracticeAgentFaqSection } from "@/pages/PracticeSettings";
import { type PracticeFull, type SettingsChangeSummary } from "@/lib/types";

/** "Souhrn" — what a practice sees instead of its settings panels.
 *
 *  The main pane is a description of what the assistant does on a call, written from the settings and not editable
 *  here or anywhere: there is no field for it, because a hand-edited summary is one that can disagree with the
 *  configuration it claims to describe. Under it sit the few settings a practice still edits directly. The assistant
 *  beside it is how everything else gets changed.
 *
 *  The same page serves both generations. A conversational practice keeps its FAQ editor here, since answers a
 *  receptionist would give are the one part of that configuration written in the practice's own words; a scripted
 *  practice has nothing below the summary and reaches its spoken lines through the assistant or the Hlášky tab.
 */
export const PracticeSummaryPage = () => {
  const practice = useOutletContext<PracticeFull>();

  return (
    <SplitPane
      storageKey="hlasemmy.souhrn.chatWidth"
      left={
        <div className="space-y-6">
          <SummaryPanel practiceId={practice.id} />
          {practice.agent && practice.agentMode === "realtime" && (
            <PracticeAgentFaqSection practiceId={practice.id} agent={practice.agent} />
          )}
          <ChangeLogPanel scope={{ kind: "practice", practiceId: practice.id }} />
        </div>
      }
      right={<SettingsChatPanel scope={{ kind: "practice", practiceId: practice.id }} />}
    />
  );
};

/** Something the practice should look at before a caller notices it.
 *
 *  Amber rather than red: each of these is a keyword check that can be wrong in the harmless direction, and a page
 *  that shouts about a compliant practice teaches people to ignore it.
 */
const Notice = ({ title, children }: { title: string; children: ReactNode }) => (
  <div className="mb-4 rounded border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
    <p className="font-medium">{title}</p>
    <p className="mt-1">{children}</p>
  </div>
);

const SummaryPanel = ({ practiceId }: { practiceId: string }) => {
  // No `staleTime`. The summary is regenerated server-side whenever the settings move, and this query is invalidated
  // the moment a change is confirmed, so caching it past that would show the practice a description of what they had
  // before they changed it.
  const query = useQuery({
    queryKey: ["practice-summary", practiceId],
    queryFn: () => api.getPracticeSummary(practiceId),
  });

  return (
    <section className="rounded border border-gray-200 bg-white p-6">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
          Jak asistentka funguje
        </h3>
        {query.data?.generatedAt && !query.data.stale && (
          <span className="text-[11px] text-gray-400">
            aktualizováno {new Date(query.data.generatedAt).toLocaleString("cs-CZ")}
          </span>
        )}
      </div>

      {query.isPending && (
        <p className="text-sm text-gray-500">Připravuji přehled podle aktuálního nastavení…</p>
      )}

      {query.isError && (
        <p className="text-sm text-red-600">
          Přehled se nepodařilo načíst ({(query.error as Error).message}).
        </p>
      )}

      {query.data?.aiDisclosureUnconfirmed && (
        <Notice title="Zkontrolujte, zda volajícím říkáte, že mluví s automatem.">
          V nastavení jsme nenašli větu, která by volajícímu dala najevo, že hovor vede automatická
          asistentka. Evropská pravidla pro umělou inteligenci to vyžadují. Napište asistentce vpravo
          „doplň, že jsi automat“ a ona to připraví.
        </Notice>
      )}

      {query.data?.recordingNoticeUnconfirmed && (
        <Notice title="Zkontrolujte, zda volajícím říkáte, že hovor nahráváte.">
          Každý hovor se nahrává, přepisuje a ukládá k požadavku. V nastavení jsme nenašli větu, která
          by to volajícímu řekla dřív, než začne mluvit o svém požadavku. Napište asistentce vpravo
          „doplň, že hovor nahráváme“ a ona to připraví.
        </Notice>
      )}

      {query.data?.stale && (
        <p className="mb-4 rounded border border-gray-200 bg-gray-50 p-2 text-xs text-gray-500">
          Nastavení se právě změnilo a text níže ho ještě nezachycuje. Zkuste stránku za chvíli znovu
          načíst.
        </p>
      )}

      {query.data?.markdown ? (
        <Markdown source={query.data.markdown} />
      ) : (
        !query.isPending &&
        !query.isError && (
          <p className="text-sm text-gray-500">
            Přehled se zatím nepodařilo připravit. Zkuste stránku načíst znovu.
          </p>
        )
      )}
    </section>
  );
};

/** What has been changed, by whom, and when.
 *
 *  This is what stands in for an undo. Each row carries the sentence the person confirming actually read, and opening
 *  one shows the whole practice configuration either side of the change — which is what somebody needs in order to put
 *  a setting back deliberately.
 */
export const ChangeLogPanel = ({ scope }: { scope: ChatScopeRef }) => {
  const [openId, setOpenId] = useState<string | null>(null);
  const query = useQuery({
    queryKey: ["settings-changes", scopeKey(scope)],
    queryFn: () => api.listSettingsChanges(scope),
  });

  if (query.data?.length === 0) return null;

  return (
    <section className="rounded border border-gray-200 bg-white p-6">
      <h3 className="mb-4 text-xs font-semibold uppercase tracking-wider text-gray-500">
        Provedené změny
      </h3>
      {query.isPending && <p className="text-sm text-gray-500">Načítám…</p>}
      {query.isError && (
        <p className="text-sm text-red-600">Chyba: {(query.error as Error).message}</p>
      )}
      <ul className="space-y-2">
        {query.data?.map((change) => (
          <li key={change.id} className="rounded border border-gray-200 p-3">
            <div className="flex items-start justify-between gap-3">
              <p className="whitespace-pre-wrap text-sm text-gray-700">{change.summary}</p>
              <button
                type="button"
                onClick={() => setOpenId(openId === change.id ? null : change.id)}
                className="shrink-0 text-xs text-[#1a2b6b] hover:underline"
              >
                {openId === change.id ? "Skrýt detail" : "Detail"}
              </button>
            </div>
            <p className="mt-1 text-[11px] text-gray-400">{changeSubtitle(change)}</p>
            {openId === change.id && <ChangeDetail scope={scope} changeId={change.id} />}
          </li>
        ))}
      </ul>
    </section>
  );
};

/** The before-and-after, as JSON.
 *
 *  Deliberately raw. This is the fallback path — somebody is here because something needs putting back, and a
 *  prettified diff that hides a field is worse than a document they can read in full. Fetched only when a row is
 *  opened, because each one carries two whole practice snapshots.
 */
const ChangeDetail = ({ scope, changeId }: { scope: ChatScopeRef; changeId: string }) => {
  const query = useQuery({
    queryKey: ["settings-change", scopeKey(scope), changeId],
    queryFn: () => api.getSettingsChange(scope, changeId),
  });

  if (query.isPending) return <p className="mt-2 text-xs text-gray-500">Načítám…</p>;
  if (query.isError)
    return <p className="mt-2 text-xs text-red-600">Chyba: {(query.error as Error).message}</p>;
  if (!query.data) return null;

  return (
    <div className="mt-3 space-y-3">
      <JsonBlock label="Co se změnilo" value={query.data.patch} />
      <JsonBlock label="Nastavení před změnou" value={query.data.before} />
    </div>
  );
};

const JsonBlock = ({ label, value }: { label: string; value: unknown }) => (
  <div>
    <p className="mb-1 text-[11px] font-medium uppercase tracking-wider text-gray-500">{label}</p>
    <pre className="max-h-64 overflow-auto rounded bg-gray-50 p-2 text-[11px] leading-relaxed text-gray-600">
      {JSON.stringify(value, null, 2)}
    </pre>
  </div>
);

const changeSubtitle = (change: SettingsChangeSummary): string => {
  const when = new Date(change.createdAt).toLocaleString("cs-CZ");
  const how = change.source === "chat" ? "přes asistentku" : "ve formuláři";
  return `${when} · ${change.actorEmail} · ${how}`;
};
